/**
 * 审计日志服务
 * 提供操作记录、查询、导出能力
 */

import { STORAGE_KEYS } from '../data/adminConfig'

const MAX_LOGS = 1000

/**
 * 记录审计日志
 * @param {object} log - 日志对象
 * @param {string} log.action - 操作类型: CREATE/UPDATE/DELETE/CONFIG_CHANGE
 * @param {string} log.targetType - 操作对象类型: expert/topic/case/config
 * @param {string} log.targetId - 操作对象ID
 * @param {string} [log.targetField] - 具体字段（配置变更时）
 * @param {any} [log.before] - 变更前值
 * @param {any} [log.after] - 变更后值
 * @param {string} [log.reason] - 操作理由
 * @param {string} [log.source='admin-ui'] - 操作来源
 * @param {object} [log.actor] - 操作人信息
 */
export function logAction(log) {
  const entry = {
    id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    timestamp: Date.now(),
    action: log.action || 'UNKNOWN',
    target: {
      type: log.targetType || 'unknown',
      id: log.targetId || '',
      field: log.targetField || null,
    },
    before: log.before !== undefined ? log.before : null,
    after: log.after !== undefined ? log.after : null,
    reason: log.reason || '',
    source: log.source || 'admin-ui',
    actor: log.actor || { role: 'unknown' },
  }

  // 读取现有日志
  const existing = getAuditLogs()
  const updated = [entry, ...existing].slice(0, MAX_LOGS)

  // 持久化
  try {
    localStorage.setItem(STORAGE_KEYS.AUDIT_LOGS, JSON.stringify(updated))
  } catch (e) {
    console.error('[Audit] Failed to persist log:', e)
  }

  return entry
}

/**
 * 获取审计日志列表
 * @param {object} [filters] - 筛选条件
 * @param {string} [filters.action] - 按操作类型筛选
 * @param {string} [filters.targetType] - 按对象类型筛选
 * @param {number} [filters.startTime] - 开始时间戳
 * @param {number} [filters.endTime] - 结束时间戳
 * @returns {Array}
 */
export function getAuditLogs(filters = {}) {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.AUDIT_LOGS)
    let logs = saved ? JSON.parse(saved) : []

    if (filters.action) {
      logs = logs.filter(l => l.action === filters.action)
    }
    if (filters.targetType) {
      logs = logs.filter(l => l.target?.type === filters.targetType)
    }
    if (filters.startTime) {
      logs = logs.filter(l => l.timestamp >= filters.startTime)
    }
    if (filters.endTime) {
      logs = logs.filter(l => l.timestamp <= filters.endTime)
    }

    return logs
  } catch {
    return []
  }
}

/**
 * 清空审计日志（危险操作，需确认）
 */
export function clearAuditLogs() {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUDIT_LOGS)
  } catch (e) {
    console.error('[Audit] Failed to clear logs:', e)
  }
}

/**
 * 导出审计日志为 JSON
 * @returns {string} JSON 字符串
 */
export function exportAuditLogs() {
  const logs = getAuditLogs()
  return JSON.stringify({
    exportedAt: Date.now(),
    count: logs.length,
    logs,
  }, null, 2)
}
